<template>
  <VModal :showing="showing" container-classes="max-w-screen-md w-full" @update:showing="$emit('close')">
    <template #header>
      <slot name="header">
        <VModalHeader>
          <template #title>Choose a 3D Presentation</template>
          <template #sub-title />
        </VModalHeader>
      </slot>
    </template>

    <template v-if="!hasNoOtherDecks">
      <VSearchableTable
        v-model:search="search"
        class="deck-selector"
        :auto-load="true"
        :options="deckList"
        :current-item="selectedDeck"
        :loading="loading"
        :count="count"
        placeholder="Search 3D Presentations"
        data-testid="deck-selector-table"
        layout="fixed"
        @selected="onDeckSelected"
        @load-more="scroll"
      >
        <template #headers>
          <th class="pl-6 w-28">&nbsp;</th>
          <th class="pl-6 w-28">&nbsp;</th>
          <th data-testid="deck-selector-title-sort" @click="sortOn('title')">
            Name
            <FontAwesomeIcon v-if="sortKey === 'title'" class="ml-2" :icon="sortDir === '-' ? 'caret-up' : 'caret-down'" />
          </th>
          <th class="pr-6 text-right" data-testid="deck-selector-date-sort" @click="sortOn('updated_at')">
            Last Updated
            <FontAwesomeIcon v-if="sortKey === 'updated_at'" class="ml-2" :icon="sortDir === '-' ? 'caret-up' : 'caret-down'" />
          </th>
        </template>
        <template #option="{ item }">
          <td>
            <img
              v-if="(item as Deck).thumbnail"
              class="w-auto h-12 rounded-md object-cover"
              :src="(item as Deck).thumbnail as string"
              :data-testid="`searchable-item-${item.id}-thumb`"
            />
            <PlaceholderDeck v-else class="rounded-md" />
          </td>
          <td class="text-center"><AccessPermissionBadge :value="(item as Deck).view_access_type" /></td>
          <td class="text-gray-800 capitalize truncate" :data-testid="`searchable-item-${item.id}-title`">{{ (item as Deck).title }}</td>
          <td class="pr-6 text-right font-normal text-sm text-gray-700 whitespace-nowrap" :data-testid="`searchable-item-${item.id}-date`">
            {{ formatDateTimeMed((item as Deck).updated_at) }}
          </td>
        </template>
      </VSearchableTable>
      <slot />
    </template>
    <template v-else>
      <div class="text-center mx-auto mt-12 mb-14 font-light text-gray-600">
        <p class="text-xl mb-4">No 3D Presentations Found!</p>
        <p class="mb-0">Please create a 3D Presentation and some slides<br />to import slides from another 3D Presentation</p>
      </div>
      <slot />
    </template>

    <template #footer>
      <slot name="footer">
        <div class="flex justify-end gap-2">
          <VButton variant="light" @click="$emit('cancel')">Cancel</VButton>
          <VButton
            v-if="isExporting && !hasNoOtherDecks"
            variant="copper"
            :disabled="!selectedDeck"
            data-testid="export-button"
            @click="$emit('export', selectedDeck)"
          >
            Export
          </VButton>
          <VButton v-if="!deckList.length" variant="copper" @click="goToDecks()">Create New 3D Presentation</VButton>
        </div>
      </slot>
    </template>
  </VModal>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'

import type { Deck } from '@/entities/deck'
import type { QueryParameters } from '@/composables/useIndexSearch'
import PlaceholderDeck from '@/components/icons/deck-placeholder.svg'
import { formatDateTimeMed } from '@/lib/datetime-format-utils'

interface ApiQuery {
  sort_by: `${SceneQuery['sortDir']}${SceneQuery['sortKey']}`
  query: string | null
  limit: number
}

interface SceneQuery extends QueryParameters {
  sortKey: 'title' | 'updated_at'
  sortDir: '-' | ''
}

const props = defineProps({
  companyId: {
    type: [String, Number],
    required: true,
  },
  showing: {
    type: Boolean,
    default: false,
  },
  deckId: {
    type: [String, Number],
    required: true,
  },
  isExporting: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['close', 'cancel', 'export', 'select-deck'])

const selectedDeck = ref<null | Deck>(null)
const deckList = computed(() => (results.value ?? []).filter((x: Deck) => x.id !== props.deckId))
const hasNoOtherDecks = computed(() => !loading.value && !search.value?.trim() && !deckList.value.length)

const {
  results,
  query: search,
  count,
  continuation,
  loading,
  sortKey,
  sortDir,
} = useIndexSearch<Deck, SceneQuery, ApiQuery>({
  initParams: { limit: 10, sortDir: '-', sortKey: 'updated_at' },
  endpoint: () => `v2/companies/${props.companyId}/decks`,
  finalParameters: ({ query, limit, sortKey, sortDir }) => ({
    sort_by: `${sortDir}${sortKey}`,
    query,
    limit,
  }),
})

function sortOn(key: SceneQuery['sortKey']) {
  if (key === sortKey.value) {
    sortDir.value = sortDir.value === '' ? '-' : ''
    return
  }
  sortKey.value = key
  sortDir.value = ''
}

async function scroll() {
  if (!loading.value && count.value > results.value.length) {
    continuation.value++
  }
}

function goToDecks() {
  window.open(`/admin/decks`, '_blank')
}

function onDeckSelected(deck: Deck) {
  selectedDeck.value = deck

  if (!props.isExporting) {
    emit('select-deck', deck)
  }
}
</script>

<style lang="stylus" scoped>
.deck-selector
  & :deep(th)
    @apply text-xs uppercase text-gray-700 bg-gray-50 px-4 py-3 font-medium border-l-0 cursor-pointer
  & :deep(td)
    @apply py-4 px-3 border-0
  & :deep(tbody tr:nth-child(even) td)
    @apply bg-gray-50
  & :deep(tbody tr:hover td)
    @apply bg-gray-200
</style>
